/**
 * bh-addon-check.js — catches paid add-ons a customer asks for in the cake notes but never ticks.
 *
 * Why: people write "с панделки и перли" in the instructions, skip the checkboxes, and pay for a
 * plain cake. The bakery then has to phone them for the difference. This asks before the cake
 * reaches the cart instead.
 *
 * Timing (decided by Vlad 2026-10-09):
 *   - Checks SILENTLY, 2s after the last keystroke in #CakeNotes. Not on blur: the field stays
 *     focused while people scroll away. Nothing appears on screen while typing.
 *   - The answer is only shown at Add to Cart, through gate(), called by the configurator's
 *     handleFormSubmit after its own validation passes.
 *   - At Add to Cart, an unanswered check is waited on for at most WAIT_MS. A slow or failed check
 *     never blocks the sale: the cake goes in and `_addon_check: skipped` tells the team.
 *
 * The model (workers/addon-check, prompt in workers/addon-check/src/prompt.js) never sees what is
 * ticked. It lists every paid add-on the note asks for; this file drops the ones selected at the
 * moment of Add to Cart. So one answer stays valid however often the customer ticks and unticks,
 * and the drawer is priced from the size chosen NOW, not when the check ran.
 *
 * Cake colour (added 2026-10-10): the Worker also returns the colour the note asks for the CAKE to be.
 * If it differs from the selected swatch, the sheet adds a "Цвят на тортата" row; "Добави" switches the
 * swatch through the configurator's own selectColor(), so the dark-colour surcharge follows as usual.
 *
 * Rules for what counts: docs/business-rules.md → "What counts as a paid add-on when a note asks
 * for it". Eval: scripts/evals/addon-notes.
 */
(function () {
  const DEBOUNCE_MS = 2000;
  const WAIT_MS = 2500;   // max extra wait at Add to Cart; Haiku answers in ~1.3s typically
  const MIN_CHARS = 10;   // "Благодаря!" is not worth a call

  const sheet = () => document.getElementById('BhAddonCheckSheet');
  const notesEl = () => document.getElementById('CakeNotes');

  // One check per distinct note. A note typed, deleted and retyped is not asked twice.
  const results = new Map(); // note -> Promise<{missing, color} | null>  (null = failed)
  const settled = new Set(); // notes whose check has answered
  let timer = null;
  let declinedFor = null;    // note the customer already said "without them" to
  let added = null;          // {note, label}: what "Добави" ticked, so a later re-check of the same note keeps it
  let gating = false;

  function design() {
    const s = sheet();
    if (!s || s.dataset.designType !== 'preset') return { type: 'custom' };
    let included = [];
    try { included = JSON.parse(s.dataset.includedAddons || '[]'); } catch (e) {}
    if (!Array.isArray(included)) included = []; // an empty metafield renders as ""
    return { type: 'preset', name: s.dataset.designName || '', description: s.dataset.designDescription || '', included_addons: included };
  }

  function check(note) {
    if (results.has(note)) return results.get(note);
    const s = sheet();
    const photo = !!document.querySelector('#BakehuntersForm [name="properties[Reference Image]"], #BakehuntersForm [name="properties[Снимка]"]')?.value;
    const p = fetch(s.dataset.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note, design: design(), photo, colors: colorList() }),
      signal: AbortSignal.timeout ? AbortSignal.timeout(9000) : undefined,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => (j && Array.isArray(j.missing)
        ? { missing: j.missing, color: typeof j.cake_color === 'string' ? j.cake_color : null }
        : null))
      .catch(() => null);
    results.set(note, p);
    p.then((v) => { if (v === null) results.delete(note); else settled.add(note); }); // failed: retry at Add to Cart
    return p;
  }

  // What the model may answer with: the swatches on offer, BG + EN so either language matches.
  function colorList() {
    const colors = (window.bhApp && window.bhApp.config && window.bhApp.config.colors) || [];
    return colors.filter((c) => c.isAvailable !== false).map((c) => ({ handle: c.handle, name: `${c.nameBG} / ${c.nameEN}` }));
  }

  const currentNote = () => (notesEl()?.value || '').trim();

  function onInput() {
    clearTimeout(timer);
    const note = currentNote();
    if (note.length < MIN_CHARS) return;
    timer = setTimeout(() => check(note), DEBOUNCE_MS);
  }

  // Hidden line-item properties. `_` keys never show to the customer; the team reads them.
  function setProp(form, key, value) {
    const name = `properties[${key}]`;
    let input = form.querySelector(`input[type="hidden"][name="${name}"]`);
    if (value == null) { if (input) input.remove(); return; }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  function money(n) {
    const lang = document.documentElement.lang || 'bg';
    return new Intl.NumberFormat(lang, { style: 'currency', currency: 'EUR' }).format(n);
  }

  /** Opens the sheet for items [{name, price}] (price 0 = no charge); resolves 'add' | 'without' | 'dismiss'. */
  function ask(app, items) {
    const s = sheet();
    const total = items.reduce((sum, it) => sum + it.price, 0);

    const list = s.querySelector('[data-bh-addon-list]');
    list.replaceChildren(...items.map((it) => {
      const li = document.createElement('li');
      li.className = 'bh-addon-check__item';
      const name = document.createElement('span');
      name.className = 'bh-addon-check__name';
      name.textContent = it.name;
      const cost = document.createElement('span');
      cost.className = 'bh-addon-check__price';
      cost.textContent = it.price > 0 ? '+' + money(it.price) : s.dataset.noChargeLabel;
      li.append(name, cost);
      return li;
    }));
    const addBtn = s.querySelector('[data-bh-addon-add]');
    addBtn.textContent = total > 0 ? s.dataset.addLabel.replace('{price}', money(total)) : s.dataset.applyLabel;

    return new Promise((resolve) => {
      let choice = 'dismiss';
      const pick = (c) => () => { choice = c; s.__bhClose ? s.__bhClose() : s.close(); };
      const onAdd = pick('add');
      const onWithout = pick('without');
      addBtn.addEventListener('click', onAdd, { once: true });
      s.querySelector('[data-bh-addon-without]').addEventListener('click', onWithout, { once: true });
      // Watch [open] rather than the `close` event: bh-drawer.js documents that the event does
      // not reliably fire for these dialogs, and a promise that never resolves freezes the ATC.
      const mo = new MutationObserver(() => {
        if (s.open) return;
        mo.disconnect();
        addBtn.removeEventListener('click', onAdd);
        s.querySelector('[data-bh-addon-without]').removeEventListener('click', onWithout);
        resolve(choice);
      });
      mo.observe(s, { attributes: true, attributeFilter: ['open'] });
      const atc = document.querySelector('#BakehuntersForm .bh-atc-btn');
      s.__bhOpen ? s.__bhOpen(atc) : s.showModal();
    });
  }

  /**
   * Called by BakehuntersConfigurator.handleFormSubmit after validation.
   * true = carry on adding to cart; false = stop, the customer is still deciding.
   */
  async function gate(app) {
    const s = sheet();
    const form = app.dom.form;
    if (!s || !form || !s.dataset.endpoint) return true;
    if (gating) return false; // a second tap while we wait
    const note = currentNote();
    setProp(form, '_addon_check', null);
    if (note.length < MIN_CHARS) { setProp(form, '_declined_addons', null); return true; }

    gating = true;
    const btn = form.querySelector('.bh-atc-btn');
    const label = btn ? btn.innerHTML : '';
    clearTimeout(timer);
    let result;
    try {
      const pending = check(note);
      // Usually already settled thanks to the 2s head start; only fresh typists see this.
      if (!settled.has(note) && btn) { btn.innerHTML = app.t('checking'); btn.setAttribute('aria-busy', 'true'); }
      result = await Promise.race([pending, new Promise((r) => setTimeout(() => r(undefined), WAIT_MS))]);
    } finally {
      if (btn) { btn.innerHTML = label; btn.removeAttribute('aria-busy'); }
      gating = false;
    }

    if (!result) { // timed out or failed
      setProp(form, '_addon_check', 'skipped');
      return true;
    }

    const selected = app.state.addons || {};
    const large = app.isLargeTier();
    const priceOf = (a) => (a ? (large ? a.priceLarge : a.priceStandard) : 0);
    const addons = result.missing
      .filter((h) => !(h in selected))
      .map((h) => (app.config.addons || []).find((a) => a.handle === h))
      .filter(Boolean);

    // Colour: only a swatch that exists and is on sale, and only when it differs from the chosen one.
    // A dark colour is charged as the dark-colors add-on; going dark -> dark or dark -> light adds nothing.
    const colors = app.config.colors || [];
    const want = result.color && colors.find((c) => c.handle === result.color && c.isAvailable !== false);
    const now = colors.find((c) => c.handle === app.state.colorHandle);
    const color = want && want.handle !== app.state.colorHandle ? want : null;
    const colorFee = color && color.isDark && !(now && now.isDark)
      ? priceOf((app.config.addons || []).find((a) => a.handle === 'dark-colors'))
      : 0;

    // `_addon_check` records what the check did, so orders show real use (2026-10-10): ok / added:… /
    // skipped. The Telegram card prints only "skipped"; the other values are for counting.
    if (!addons.length && !color) { setProp(form, '_declined_addons', null); setProp(form, '_addon_check', added && added.note === note ? added.label : 'ok'); return true; }
    if (declinedFor === note) { setProp(form, '_addon_check', 'declined'); return true; } // already answered "without them" for this exact note

    window.bhClarityEvent && window.bhClarityEvent('bh_addon_check_shown');
    const items = addons.map((a) => ({ name: app.localeName(a), price: priceOf(a) }));
    if (color) items.push({ name: sheet().dataset.colorLabel.replace('{color}', app.localeName(color)), price: colorFee });
    const choice = await ask(app, items);

    if (choice === 'without') {
      declinedFor = note;
      const declined = addons.map((a) => a.nameBG);
      if (color) declined.push(`Цвят: ${color.nameBG}`);
      setProp(form, '_declined_addons', declined.join(', '));
      setProp(form, '_addon_check', 'declined');
      window.bhClarityEvent && window.bhClarityEvent('bh_addon_check_without');
      return true;
    }
    if (choice === 'add') {
      addons.forEach((a) => {
        const cb = document.querySelector(`.bh-addon-row[data-handle="${a.handle}"] .bh-addon-checkbox`);
        if (cb && !cb.checked) { cb.checked = true; cb.dispatchEvent(new Event('change', { bubbles: true })); }
      });
      if (color) app.selectColor(color.handle); // same path as tapping the swatch: surcharge, label, total
      setProp(form, '_declined_addons', null);
      added = { note, label: 'added: ' + addons.map((a) => a.handle).concat(color ? ['color:' + color.handle] : []).join(', ') };
      setProp(form, '_addon_check', added.label);
      window.bhClarityEvent && window.bhClarityEvent('bh_addon_check_added');
      // A text add-on (Страничен надпис) needs its words before it can be bought: send the
      // customer to the field instead of adding an empty one.
      const problem = app.validateConfiguration();
      if (problem) { app.flagMissing(problem); return false; }
      return true;
    }
    return false; // closed the sheet: stay on the page
  }

  function boot() {
    const el = notesEl();
    if (!el || !sheet()) return;
    el.addEventListener('input', onInput);
    onInput(); // a note restored by the browser or by cart-edit still gets checked
  }

  window.bhAddonCheck = { gate };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
